/**
 * Pre-bootstrap AuthKit gate.
 *
 * Runs synchronously from main.js BEFORE either the Vue legacy app or the
 * React newui runtime mounts. When the deployed auth provider is WorkOS
 * and the user is on a legacy Vue auth page (/login, /register,
 * /forgot-password, /reset-password) the legacy form would flash for a
 * frame before the page's onMounted hook navigated away to AuthKit.
 *
 * This gate prevents that flash by:
 *   1. Detecting the auth-page path + provider synchronously.
 *   2. Injecting a branded "Signing you in…" splash into the DOM root.
 *   3. Calling window.location.assign(<AuthKit URL>) immediately.
 *   4. Returning true so main.js bails out before Vue/React mount.
 *
 * The `?provider=keycloak` escape hatch keeps the legacy form available
 * during the migration window — when set, this gate is a no-op and the
 * regular Vue routing flow runs.
 */
import {
  buildWorkOSAuthorizeUrl,
  createAndStoreWorkOSState,
  getEnvAuthProviderJs,
} from '../utils/workosLogin.js'

const AUTH_PAGE_PATHS = new Set([
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
])

const SPLASH_HTML = `
<div role="status" aria-live="polite" data-testid="auth-splash" style="
  position: fixed; inset: 0; display: flex; flex-direction: column;
  align-items: center; justify-content: center; gap: 18px;
  background: linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%);
  color: #1f2937; font-family: ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif;
  z-index: 9999;
">
  <h1 style="margin: 0; font-size: 1.875rem; font-weight: 700; letter-spacing: -0.025em;">
    <span style="color: #4f46e5;">Proof</span><span style="color: #1f2937;">Deck</span>
  </h1>
  <div aria-hidden="true" style="
    width: 36px; height: 36px;
    border: 3px solid rgba(79, 70, 229, 0.18);
    border-top-color: #4f46e5;
    border-radius: 50%;
    animation: pd-auth-splash-spin 0.9s linear infinite;
  "></div>
  <p style="margin: 0; font-size: 0.875rem; color: #4b5563;">Signing you in…</p>
  <style>
    @keyframes pd-auth-splash-spin { to { transform: rotate(360deg); } }
    @media (prefers-color-scheme: dark) {
      [data-testid="auth-splash"] {
        background: linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%) !important;
        color: #f1f5f9 !important;
      }
      [data-testid="auth-splash"] p { color: #cbd5e1 !important; }
    }
  </style>
</div>
`

function getQueryParams() {
  try {
    return new URLSearchParams(window.location.search)
  } catch {
    return new URLSearchParams('')
  }
}

function workosOverrideActive(params) {
  const inline = params.get('provider') === 'keycloak'
  if (inline) {
    try {
      sessionStorage.setItem('login_provider_override', 'keycloak')
    } catch {
      // sessionStorage unavailable — fall back to inline-only override
    }
    return true
  }
  try {
    return sessionStorage.getItem('login_provider_override') === 'keycloak'
  } catch {
    return false
  }
}

function getAuthKitConfig() {
  const env = (typeof import.meta !== 'undefined' && import.meta.env) || {}
  const clientId = env.VITE_WORKOS_CLIENT_ID
  const redirectUri = env.VITE_WORKOS_REDIRECT_URI
  if (!clientId || !redirectUri) return null
  return { clientId, redirectUri }
}

/**
 * Compute the AuthKit hosted URL for a given Vue auth-page path. Returns
 * null when the path is not an auth page or required config is absent.
 *
 * Mapping:
 *   /register                                  -> screen_hint=sign-up
 *   /login | /forgot-password | /reset-password -> default (sign-in)
 *
 * AuthKit's sign-in screen has its own "Forgot password?" link, so we
 * route /forgot-password and /reset-password through sign-in as well —
 * password lifecycle is owned by WorkOS in the WorkOS configuration.
 */
export function planAuthKitRedirect(pathname, params) {
  if (!AUTH_PAGE_PATHS.has(pathname)) return null
  const config = getAuthKitConfig()
  if (!config) return null

  const inviteToken = params.get('invite')
  const inviteEmail = params.get('email')
  if (pathname === '/register' && inviteToken) {
    try {
      sessionStorage.setItem('pending_invite_token', String(inviteToken))
    } catch {
      // sessionStorage unavailable — invite acceptance will need manual link
    }
  }

  const state = createAndStoreWorkOSState()
  return buildWorkOSAuthorizeUrl({
    clientId: config.clientId,
    redirectUri: config.redirectUri,
    state,
    screenHint: pathname === '/register' ? 'sign-up' : undefined,
    loginHint: inviteEmail ? String(inviteEmail) : undefined,
  })
}

function showSplash() {
  try {
    const target = document.getElementById('app') || document.body
    if (target) {
      target.innerHTML = SPLASH_HTML
    }
  } catch {
    // DOM not ready — splash is best-effort; redirect still proceeds.
  }
}

/**
 * Synchronous entrypoint. Returns true when the gate has taken over
 * (caller must NOT mount Vue/React). Returns false when the regular
 * bootstrap should proceed.
 */
export function applyPreBootstrapAuthGate({
  pathname = window.location.pathname,
  navigate = (url) => window.location.assign(url),
} = {}) {
  if (getEnvAuthProviderJs() !== 'workos') return false
  if (!AUTH_PAGE_PATHS.has(pathname)) return false

  const params = getQueryParams()
  if (workosOverrideActive(params)) return false

  const url = planAuthKitRedirect(pathname, params)
  if (!url) return false

  showSplash()
  navigate(url)
  return true
}

// Exported for tests
export const __testing = { AUTH_PAGE_PATHS, SPLASH_HTML }
